import { api } from './api.config';

import {
  TournamentState,
  User,
  Group,
  Match,
  MatchResult,
  Class,
  Tour,
  Court,
  TournamentCategory,
} from '../types/tournament';
import { TournamentsState } from '../features/tournaments/tournamentsSlice';
import { initialTournamentData } from '../constant/tournament';
import { ImageFile } from '../types/uploadFile';
import { TournamentMatchStatusEnum } from '../types/enums';
import { ClassFormatEnum, GroupStandingsTieBreakerEnum } from '../types/enums';
import {
  IMatchScheduleItem,
  IMatchScheduleItemDto,
  IScheduleConstraints,
  IScheduleJobStatusResponse,
  ScheduleAndUnscheduledMatches,
} from '../types/schedule';
import { ClassTeam } from '../pages/tourDetails/TournamentClasses/components/ClassesTag/ClassesTag';
import {
  IStandingGroup,
  IStandingRound,
  CategoryStanding,
  CategoryStandingGroup,
} from '../pages/TournamentScorecardPage/TournamentScorecardPage.types';
import { IMatch } from '../pages/tourDetails/TournamentPlay/components/PlayTag/PlayTag';
import { GroupStandingsTieBreakerResolution } from '../utils/groupStandingsTieBreakers';

export type { CategoryStanding, CategoryStandingGroup };

export interface IStream {
  id: number;
  courtId: number;
  startDate: Date;
  endDate: Date;
  category: string;
  name: string;
  description: string;
  image: string;
  url: string;
}

export interface ITournamentStats {
  totalClasses: number;
  totalPlayers: number;
  totalTickets: number;
  totalMatches: number;
}

export type IMatchBoardItem = {
  id: number;
  tournamentId: number;
  matchIdToShow: string;
  startDate: string;
  endDate: string;
  scheduleEndDate: string | null;
  courtName: string;
  awayPlayer1: string;
  awayPlayer2: string | null;
  awayTeamName: string;
  homePlayer1: string;
  homePlayer2: string | null;
  homeTeamName: string;
  tournamentClassName: string;
  results: string[];
  status: TournamentMatchStatusEnum;
};

export interface IGetMatchBoardForTournament {
  matchBoard: IMatchBoardItem[];
  total: number;
}

interface IGetMatchBoardForTournamentParams {
  id: number;
  search?: string;
  take?: number;
  skip?: number;
  classId?: number | null;
}

export interface IPlayersInfoAPI {
  playersInfo: IPlayersInfo[];
  tournament: {
    name: string;
  };
}

export type ContactUser = {
  id: number;
  dateOfBirth?: string;
  duprId?: string;
  duprRatingSingles?: number | null;
  duprRatingDoubles?: number | null;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  licenseNumber?: string;
  userName?: string;
  countryCode?: string;
};

export interface IPlayersInfo {
  id: number;
  name: string;
  teams: { name: string; seed: number; type: number; users: ContactUser[] }[];
}

export interface ClassStandingsResponse {
  classId: number;
  format: ClassFormatEnum;
  players: User[];
  matches: {
    upcoming: IMatch[];
    ongoing: IMatch[];
    completed: IMatch[];
  };
  roundStandings: IStandingRound[];
  groupStandings: IStandingGroup[];
  categoryStandings?: CategoryStanding[];
  hideGroupStandings?: boolean;
  groupStandingsTieBreakers?: GroupStandingsTieBreakerEnum[];
  groupStandingsTieBreakerResolution?: GroupStandingsTieBreakerResolution;
}

interface IClassesInfoAPI {
  classesInfo: IClassesInfo[];
}

export interface IClassesInfo {
  currency: string;
  maxPlayers: number;
  name: string;
  price: number;
  registered: number;
  vat: number;
}

export enum TicketStatusEnum {
  /** User has started payment process (Started filling out payment details), we reserve the ticket for 10minutes */
  RESERVED = 1,
  /** Payment is processing (we have received payment details, but not yet confirmed) */
  PROCESSING = 2,
  /** Payment was confirmed */
  PAID = 3,
  /** Admin triggered a refund */
  REFUNDED = 4,
  /** Payment was never finished (cancelled by user before payment was done, or timed out without receiving payment details)  */
  CANCELLED = 5,
  /** Ticket was transferred to another user */
  TRANSFERRED = 6,
  /** Ticket has been paid. An Admin has disabled the ticket (for example if the player has withdrawn from the tournament last minute), or the player is removed from the tournament for another reason (without refund) */
  DISABLED = 7,
}

/** One money line in the tournament earnings ledger. The backend does all sign/VAT/fee
 *  math; the frontend renders these row-for-row and never re-sums them. */
export type LedgerEntryType = 'ticket' | 'registration_fee' | 'refund';

export interface ILedgerEntry {
  type: LedgerEntryType;
  ticketId: number | null; // null for registration_fee
  userId: number;
  firstName: string | null;
  lastName: string | null;
  purchasedTicket: string | null; // username fallback
  class: string | null; // null for registration_fee
  paymentId: string | null;
  currency: string | null; // lowercase ISO, e.g. "sek"
  amount: number; // SIGNED: + ticket/fee, − refund
  vat: number; // signed, same sign as amount
  status: TicketStatusEnum | null; // set for ticket lines, else null
  date: string | null;
}

export interface IEarnings {
  currency: string | null;
  classRevenue: number;
  registrationFeesTotal: number;
  gross: number; // classRevenue + registrationFeesTotal
  raqtFee: number; // RAQT's cut (tickets AND reg fees)
  refundsTotal: number; // positive
  organizerNet: number; // gross − raqtFee − refundsTotal
  classVatTotal: number;
  registrationFeesVatTotal: number;
  vatTotal: number; // classVatTotal + registrationFeesVatTotal
  serviceFeeRate: number; // tournament RAQT fee % (e.g. 10) — never null
  vatRate: number | null; // single VAT % if uniform, else null (mixed)
}

export interface IRaqtInvoice {
  id: number | null;
  invoiceAddress: string | null;
  invoiceEmail: string | null;
  organizationNumber: string | null;
  raqtVAT: string | null;
}

export interface IFetchTicketsInfoAPI {
  /** Table source — render row-for-row, no client-side money math. */
  ledger: ILedgerEntry[];
  /** Totals source for the earnings summary card. */
  earnings: IEarnings;
  /** RAQT invoice/billing details. */
  raqtDetails: IRaqtInvoice;
}

export interface IPublicTournament {
  id: number;
  name: string;
  description: string;
  startDate: Date | string;
  endDate: Date | string;
  registrationOpen: Date | string;
  registrationClose: Date | string;
  location: string;
  img: string;
  icon: string;
  featureImg: string;
  country?: string;
  timeZone?: string;
  creator: ICreator;
  sections: ISection[];
}

export interface ICreator {
  firstName: string;
  lastName: string;
}

export interface ISection {
  id: number;
  title: string;
  description: string;
}

export interface IMatchBoardGroupedByClass {
  [classId: number]: IMatchScheduleItem[];
}

export type UploadPath =
  | 'tournament'
  | 'team'
  | 'organization'
  | 'sponsor'
  | 'paddle'
  | 'avatar'
  | 'logo';

export const fetchPublicTournamentAPI = async (): Promise<TournamentState> => {
  const response = await api.get('tournament/tournaments-short-info');
  return response.data;
};

export const fetchStreamsAPI = async (): Promise<IStream[]> => {
  const response = await api.get('tournament/raqt-tv');
  return response.data;
};

export const fetchTicketsInfoAPI = async (id: number): Promise<IFetchTicketsInfoAPI> => {
  const response = await api.get(`tournament/${id}/tickets-info`);
  return response.data;
};

export const fetchPlayersInfoAPI = async (id: number): Promise<IPlayersInfoAPI> => {
  const response = await api.get(`tournament/${id}/players-info`);
  return response.data;
};

export const fetchClassesInfoAPI = async (id: number): Promise<IClassesInfoAPI> => {
  const response = await api.get(`tournament/${id}/classes-info`);
  return response.data;
};

export const fetchTournamentData = async (id: number): Promise<TournamentState> => {
  const response = await api.get(`tournament/${id}`);
  return response.data;
};

export const fetchPublicViewTournamentAPI = async (id: number): Promise<IPublicTournament> => {
  const response = await api.get(`tournament/${id}/view`);
  return response.data;
};

export const fetchTournamentInfoData = async (id: number): Promise<TournamentState> => {
  const response = await api.get(`tournament/${id}/tournament-info`);
  return response.data;
};

export const fetchTournamentStatsAPI = async (id: number): Promise<ITournamentStats> => {
  const response = await api.get(`tournament/${id}/summery`);
  return response.data;
};

export const fetchClassesByTournamentId = async (id: number): Promise<Array<Class>> => {
  const response = await api.get(`tournament/${id}/classes`);
  return response.data;
};

export const fetchClassesByTournamentIdAndClassId = async (classId: number): Promise<Class> => {
  const response = await api.get(`tournament/class/${classId}`);
  return response.data;
};

export const fetchClassListByTournamentId = async (id: number): Promise<Array<Class>> => {
  const response = await api.get(`tournament/${id}/classes-list`);
  return response.data;
};

export const fetchTournamentWithClassListByTournamentId = async (
  id: number
): Promise<TournamentState> => {
  const response = await api.get(`tournament/${id}/with-class-list`);
  return response.data;
};

export const fetchTournamentWithClassAndCourtListByTournamentId = async (
  id: number
): Promise<TournamentState> => {
  const response = await api.get(`tournament/${id}/info-with-class-and-court-list`);
  return response.data;
};

export const fetchClassStandingsByClassId = async (
  id: number,
): Promise<ClassStandingsResponse> => {
  const response = await api.get(`tournament/class/${id}/standings`);
  return response.data;
};

export const fetchClassFullTeamExtras = async (classId: number) => {
  const response = await api.get(`tournament/class/${classId}/full`);
  return {
    categoryStandings: (response.data.categoryStandings ?? []) as CategoryStanding[],
    hideGroupStandings: Boolean(response.data.hideGroupStandings),
  };
};

export const fetchTournamentsData = async (
  organizationId: number | string
): Promise<Array<TournamentsState>> => {
  const response = await api.get('tournament', { params: { organization: organizationId } });
  return response.data;
};

export const updateTournamentData = async (
  id: number,
  organization: number,
  data: any
): Promise<TournamentState> => {
  // TODO: temporary fields: organization & creator, because out of registration
  const response = await api.patch<TournamentState>(`tournament/${id}`, {
    ...data,
    organization,
  });
  return response.data;
};
export const updateTournamentInfoData = async (
  id: number,
  organization: number,
  data: any
): Promise<TournamentState> => {
  const response = await api.patch<TournamentState>(`tournament/${id}/tournament-info`, {
    ...data,
    organization,
  });
  return response.data;
};

export const fetchUsersData = async (userName: string = '') => {
  const response = await api.get<User[]>('user/search', {
    params: { search: userName },
  });
  return response.data;
};

export const fetchTeamsData = async (communityId: string, searchTerm: string = '') => {
  const response = await api.get<ClassTeam[]>('user/search-teams', {
    params: { limit: 10, organizationId: Number(communityId), searchTerm },
  });
  return response.data;
};

export const updatePlayData = async (
  classId: number,
  data: any
): Promise<{ id: number; tournamentGroups: Group[]; tournamentMatches: Match[]; tournamentCategories: TournamentCategory[]; }> => {
  const response = await api.patch(`tournament/class-play/${classId}`, data);
  return response.data;
};

export const updatePlayStatus = async (
  matchId: number,
  data: { status: number }
): Promise<Match> => {
  const response = await api.patch(`tournament/match-status/${matchId}`, data);
  return response.data;
};

export const updateMatchStatusData = async (
  matchId: number,
  data: { results: Array<MatchResult> }
): Promise<{
  id: number;
  results: Array<MatchResult>;
}> => {
  const response = await api.patch(`tournament/match-result/${matchId}`, data);
  return response.data;
};

export const createTournament = async (organizationId: number): Promise<TournamentState> => {
  const response = await api.post<TournamentState>('tournament', {
    ...initialTournamentData,
    organization: organizationId,
  });
  return response.data;
};

export const duplicateTournament = async (id: number): Promise<TournamentState> => {
  const response = await api.post<TournamentState>(`tournament/duplicate/${id}`);
  return response.data;
};

export const deleteTournament = async (id: number): Promise<{ affected: 0 | 1; id: number }> => {
  const response = await api.delete<{ affected: 0 | 1 }>(`tournament/${id}`);
  return { ...response.data, id };
};

export const safeDeleteTournament = async (
  id: number
): Promise<{ affected: 0 | 1; id: number }> => {
  const response = await api.delete<{ affected: 0 | 1 }>(`tournament/safe-delete/${id}`);
  return { ...response.data, id };
};

export const uploadImage = async (data: FormData, name: UploadPath): Promise<ImageFile> => {
  const response = await api({
    method: 'post',
    url: `upload/${name}`,
    data,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const generateMatchesData = async (classId: number): Promise<Class> => {
  const response = await api.post(`tournament/class-generate-matches/${classId}`);
  return response.data;
};

export const getListTour = async (): Promise<Tour[]> => {
  const response = await api.get(`tour`);
  return response.data;
};

export const generateGroupsRequest = async (classId: number): Promise<any> => {
  const response = await api.post(`tournament/class-generate-groups/${classId}`);
  return response.data;
};

export const generateCategoriesByClass = async (classId: number): Promise<any> => {
  const response = await api.post(`tournament/class-generate-categories/${classId}`);
  return response.data;
};

export const bulkDeleteGroupsByClass = async (classId: number): Promise<any> => {
  const response = await api.delete(`tournament/class-bulk-delete-by-id/${classId}`);
  return response.data;
};

export const deleteClass = async (classId: number): Promise<any> => {
  const response = await api.delete(`tournament/class/${classId}`);
  return response.data;
};

export const getMatchBoardForTournament = async ({
  id,
  search = '',
  take,
  skip,
  classId,
}: IGetMatchBoardForTournamentParams): Promise<IGetMatchBoardForTournament> => {
  const response = await api.get(`tournament/${id}/match-board`, {
    params: {
      search: search.toLocaleLowerCase(),
      take,
      skip,
      classId,
    },
  });
  return response.data;
};

export const getMatchBoardGroupedByClassForTournament = async (
  id: number
): Promise<IMatchBoardGroupedByClass> => {
  const response = await api.get(`tournament/${id}/match-board-grouped-by-class`);
  return response.data;
};

export const createTournamentSchedule = async (
  id: number,
  constraints: IScheduleConstraints
): Promise<ScheduleAndUnscheduledMatches> => {
  const response = await api.post(`tournament/${id}/create-match-schedule`, constraints);
  return response.data;
};

export const getTournamentSchedule = async (id: number): Promise<ScheduleAndUnscheduledMatches> => {
  const response = await api.get(`tournament/${id}/get-match-schedule`);
  return response.data;
};

export const updateTournamentSchedule = async (
  id: number,
  constraints: IScheduleConstraints,
  matchScheduleItems: IMatchScheduleItemDto[]
): Promise<any> => {
  return await api.patch(`tournament/${id}/update-match-schedule`, {
    matchScheduleItems,
    constraints,
  });
};

export const publishTournamentSchedule = async (
  id: number,
  constraints: IScheduleConstraints,
  matchScheduleItems: IMatchScheduleItemDto[]
): Promise<ScheduleAndUnscheduledMatches> => {
  const response = await api.post(`tournament/${id}/publish-schedule`, {
    matchScheduleItems,
    constraints,
  });
  return response.data;
};

export const getScheduleConstraints = async (id: number): Promise<IScheduleConstraints> => {
  const response = await api.get(`tournament/${id}/get-schedule-constraints`);
  return response.data;
};

export const removeTournamentSchedule = async (id: number): Promise<any> => {
  return await api.patch(`tournament/${id}/remove-match-schedule`);
};

export const cancelTournamentScheduleGeneration = async (
  id: number
): Promise<{ success: boolean; message: string }> => {
  const response = await api.post(`tournament/${id}/cancel-match-schedule`);
  return response.data;
};

export const getScheduleJobStatus = async (
  id: number
): Promise<IScheduleJobStatusResponse> => {
  const response = await api.get(`tournament/${id}/schedule-job-status`);
  return response.data;
};